/*
 * Ctrl/Cmd+K: type a few letters, land anywhere (spec 12 §4.5). It searches core's own destinations
 * and whatever every module offers through its `quickSwitcherProviders` slot — conversations and
 * people are chat's rows, because their routes are chat's, and core never spells a module's path.
 *
 * It is a **command palette and not a dialog** (VISUAL.md §6.13): no title bar, no close button, no
 * hint line under the field — one field, rows of one shape under their groups, and a footer that says
 * which three keys act. The furniture `ui/Dialog` still draws is hidden by `quickSwitcher.css`, which
 * is also where the panel's geometry lives.
 *
 * It is a combobox, not a list of links: the input keeps focus while the arrows move the selection,
 * `aria-activedescendant` says which row is meant, and Enter navigates. Matching is a subsequence
 * scan — "dv" finds "#dev" — because a person types the letters they remember, not a prefix.
 *
 * Since the rail became places only (UX §1.1) this is also the fallback for everything it stopped
 * carrying: People & groups, Settings, Security and the keyboard sheet are rows here, and so is
 * Administration for whoever has it — the same filter by role the rail applies, because a row that
 * leads to a refusal is worse than no row.
 */

import { useNavigate, useRouterState } from "@tanstack/react-router"
import { useEffect, useId, useMemo, useState } from "react"
import { readJSON, storageKey, writeJSON, writeValue } from "../../lib/storage.ts"
import { conversationHref } from "../../routes/links.ts"
import { Dialog, emojiText } from "../../ui/index.ts"
import { FILTER_REPLIES, setPendingFilter } from "../activity/activityState.ts"
import { formatNumber, t, translate, type PlainKey } from "../i18n/index.ts"
import { useSlot } from "../modules/slots.ts"
import { AccountPill, chipPerson, PersonFace } from "../people/PersonChip.tsx"
import { isAdmin } from "../people/standing.ts"
import type { ConversationView } from "../store/model.ts"
import { useModel } from "./context.tsx"
import { Icon, type IconName } from "./icons.tsx"
import "./quickSwitcher.css"
import { useOpenShortcutSheet } from "./ShortcutProvider.tsx"

/**
 * What a module offers: a thing with an address. The shape matches `QuickSwitcherProvider.query`
 * in `core/modules/registry.ts`, which spells it out rather than importing it — the registry is
 * below the shell in the layering and may not reach up into it.
 */
export interface SwitchTarget {
	id: string
	label: string
	detail?: string
	to: string
	/**
	 * The account a row stands for, when it stands for one. A person's row then draws the face the
	 * rest of the product draws them with (`core/people/PersonChip.tsx`) instead of a line of text —
	 * which is what makes a list of channels and people scannable at all. The row reads the account
	 * out of the model itself, so a provider hands over the id and nothing else.
	 */
	userId?: number
	/**
	 * What is waiting behind the row, where the provider knows it: the unread count the place's own
	 * badge shows (X-B1 A2). Core's own rows carry none — a page has nothing waiting in it — and the
	 * count cannot be worked out here, because the cursors live in a module's slice and the shell does
	 * not read a module's state.
	 */
	count?: number
	/** True when some of that count names the reader: the one pill drawn in the accent. */
	mention?: boolean
	/**
	 * The heading this row is drawn under, as a catalogue key — the module's own
	 * (`aichat.switcher.group`). Core's four groups are about conversations, people and pages,
	 * and a thing that is none of the three was drawn under *Channels*; a module that brings a new
	 * kind of destination therefore names what to call a run of them, and its groups follow core's.
	 * The key is rendered with `translate`, which resolves any registered namespace.
	 */
	groupTitleKey?: string
	/** The glyph of the 24 px slot, where the row is not a conversation core can recognise. */
	icon?: IconName
}

/** The groups, in the order the list shows them. A row belongs to exactly one. */
const GROUPS = ["recent", "channels", "people", "places"] as const

type SwitchGroup = (typeof GROUPS)[number]

const GROUP_TITLES: Record<SwitchGroup, PlainKey> = {
	recent: "shell.quickSwitcher.group.recent",
	channels: "shell.quickSwitcher.group.channels",
	people: "shell.quickSwitcher.group.people",
	places: "shell.quickSwitcher.group.places",
}

/**
 * A row of the list. Core's own rows may act in place instead of navigating — *Keyboard shortcuts*
 * opens a sheet, and there is no address for a sheet — which is why this is not `SwitchTarget`.
 */
export interface SwitchRow {
	id: string
	label: string
	detail?: string
	to?: string
	/** The account this row is about; see `SwitchTarget.userId`. */
	userId?: number
	/** What is waiting behind the row, and whether it names the reader; see `SwitchTarget.count`. */
	count?: number
	mention?: boolean
	run?: () => void
	/** Runs before the navigation: the state the destination opens with (Threads' chip). */
	prepare?: () => void
	/**
	 * Where the row is shown: one of core's four groups, or a module's own heading key (see
	 * `SwitchTarget.groupTitleKey`), whose runs are drawn after them. `recent` is assigned by the
	 * list and never by the row's maker.
	 */
	group: SwitchGroup | (string & {})
	/** The glyph of the 24 px slot, for a row that is not one person. */
	icon: IconName
}

/** One group as the list draws it: its own key, the key of its heading, and the rows under it. */
interface RowGroup {
	group: string
	/** The catalogue key of the heading: core's for core's four, the group's own for a module's. */
	titleKey: string
	rows: SwitchRow[]
}

/** What a run of rows is headed. A group core does not own is itself the key of its heading. */
function titleOfGroup(group: string): string {
	return GROUP_TITLES[group as SwitchGroup] ?? group
}

/** How many rows one opening offers, and how many of them the *Recent* group may take. */
const MAX_ROWS = 20
const RECENT_SHOWN = 5

/**
 * The three keys the footer prints. They are keycaps rather than copy — the same mark in every
 * language — and they stand inside a `<kbd>`, which is the monospace stack: whatever the interface
 * face carries, an arrow is drawn.
 */
const KEY_MARKS = { enter: "↵", up: "↑", down: "↓" } as const

/**
 * The addresses this device has been to, newest first — what *Recent* is made of (U15/A6).
 *
 * It lives in the browser's own storage rather than in the model or in a variable of this file: the
 * five places somebody keeps coming back to are a per-device convenience like the theme and the pane
 * widths, and a reload must not be what empties the group a palette is opened for. It shares the
 * kind `routes/links.ts` remembers the last conversation under, because it is the same fact about
 * the same reader, kept for five addresses instead of one.
 */
const VISITED_KEY = storageKey("lastRoute", "recent")

/** Enough addresses that five of them are still rows the providers offer. */
const VISITED_KEPT = 12

function visitedPaths(): string[] {
	const stored = readJSON<unknown>(VISITED_KEY)
	if (!Array.isArray(stored)) return []
	return stored.filter((entry): entry is string => typeof entry === "string")
}

function noteVisited(path: string): void {
	const paths = visitedPaths()
	if (paths[0] === path) return
	writeJSON(VISITED_KEY, [path, ...paths.filter((entry) => entry !== path)].slice(0, VISITED_KEPT))
}

/** Forgets where this device has been; a test's way back to a switcher that has been nowhere. */
export function forgetVisited(): void {
	writeValue(VISITED_KEY, "")
}

/**
 * A subsequence match, scored so that an earlier and tighter run wins: "dev" beats "d-e-v" spread
 * across a long name. Returns null when a letter is missing.
 */
export function scoreTarget(term: string, label: string): number | null {
	if (term === "") return 0
	const needle = term.toLowerCase()
	const hay = label.toLowerCase()
	let score = 0
	let at = -1
	for (const letter of needle) {
		const found = hay.indexOf(letter, at + 1)
		if (found < 0) return null
		score += found === at + 1 ? 2 : 0
		score -= found - at - 1
		at = found
	}
	return score - hay.length / 100
}

/**
 * Whether any word of the text begins with what was typed. It is the match a person means when they
 * type three letters: "dor" means Dora, and only if nobody is called that may the subsequence match
 * answer with *Keyboar**d** sh**or**tcuts* (X-B4 B4-10, VISUAL.md §8 U15).
 *
 * A word begins at the start of the text, after a space, or after one of the characters this product
 * starts a name with: the hash of a channel, the at of a handle, and the dash and plus a slug and a
 * group handle are written with. The chat provider applies the same rule to its own rows before it
 * hands them over; this is the shell's half, over every row of every provider, and the two cannot be
 * one function because the module layer may not import the shell's internals and the shell may not
 * reach into a module.
 */
export function startsAWord(term: string, text: string): boolean {
	const needle = term.toLowerCase()
	const hay = text.toLowerCase()
	for (let at = hay.indexOf(needle); at >= 0; at = hay.indexOf(needle, at + 1)) {
		if (at === 0 || /[\s#@+-]/.test(hay.charAt(at - 1))) return true
	}
	return false
}

/**
 * The rows one term leaves, in two bands (B4-10): a row whose text *begins* with what was typed, and
 * a row that merely contains its letters in order. The second band is offered only when the first is
 * empty, and inside a band nothing is re-sorted — which is the whole point. A provider ranks its own
 * rows with everything it knows (the sidebar's order, starred first, the reader's own membership), and
 * the switcher used to throw that away by re-scoring every row it was handed: typing "dor" then
 * offered *Dora Degen* and, under her, *Keyboard shortcuts*.
 *
 * With an empty box every row survives in the order it arrived, which is what makes a freshly opened
 * palette the list of where this reader actually is.
 */
export function rankRows(rows: SwitchRow[], term: string): SwitchRow[] {
	if (term.trim() === "") return rows
	const prefix: SwitchRow[] = []
	const fuzzy: SwitchRow[] = []
	for (const row of rows) {
		const text = `${row.label} ${row.detail ?? ""}`
		if (scoreTarget(term, text) === null) continue
		if (startsAWord(term, text)) prefix.push(row)
		else fuzzy.push(row)
	}
	return prefix.length > 0 ? prefix : fuzzy
}

/**
 * The destinations core itself owns; a module adds its own through the slot. A row without a `to` acts
 * in place — the keyboard sheet — and the one marked `adminOnly` is shown to an administrator alone.
 * `prepare` is what a row runs before the navigation: *Threads* is the Activity page under its
 * *Replies* chip (X-A4 N2), and which chip a page opens with is a hand-over and not a route
 * parameter (`core/activity/activityState.ts`).
 */
const CORE_DESTINATIONS = [
	{ id: "activity", to: "/activity", labelKey: "shell.nav.activity", icon: "activity" },
	{
		id: "threads",
		to: "/activity",
		labelKey: "activity.threads.title",
		icon: "threads",
		prepare: true,
	},
	{ id: "search", to: "/search", labelKey: "shell.nav.search", icon: "search" },
	{ id: "people", to: "/people", labelKey: "shell.nav.people", icon: "people" },
	{ id: "settings", to: "/settings/appearance", labelKey: "shell.nav.settings", icon: "settings" },
	{ id: "security", to: "/settings/security", labelKey: "auth.account.security", icon: "lock" },
	{ id: "shortcuts", labelKey: "shell.shortcuts.title", icon: "keyboard" },
	{ id: "admin", to: "/admin/people", labelKey: "shell.nav.admin", icon: "admin", adminOnly: true },
] as const satisfies {
	id: string
	to?: string
	labelKey: PlainKey
	icon: IconName
	adminOnly?: boolean
	prepare?: boolean
}[]

/**
 * What kind of place a provider's row leads to and how its two halves read, worked out from the
 * model rather than asked of the provider: core knows the address of every conversation the reader
 * is in (`routes/links.ts`), so the row carrying that address *is* that conversation — and a
 * provider stays a list of names and addresses.
 *
 * Two things follow from recognising it. The slot draws `lock` for a private channel and a face for
 * a direct conversation, where a row core does not recognise — a channel nobody has joined — is a
 * `hash`. And a channel with a display name of its own is turned round: a provider names it by its
 * address, because that is what its sidebar row says, while a palette reads best with the name first
 * and the address quietly after it ("Engineering · #eng", §6.13). A channel whose name is only its
 * slug capitalised has no second half to turn — "#design · design" is one thing said twice — and
 * then the address stays in front, because that is how the sidebar, the band and a mention all spell
 * it, and this list is the surface a reader looks at most (X-A3 V21). The channel nobody has joined
 * keeps the provider's shape too: its second half is not a name but the sentence that says why the
 * row will not simply open.
 *
 * A row that names a heading of its own is none of those things — an AI chat, and whatever comes
 * after it — so it is taken as the provider wrote it: its two halves are the title and the path, in
 * that order, and only the module knows which is which.
 */
export function describeRow(
	target: SwitchTarget,
	byAddress: Map<string, ConversationView>,
): SwitchRow {
	const { detail: given, groupTitleKey, icon, ...rest } = target
	if (groupTitleKey !== undefined) {
		return {
			...rest,
			...(given === undefined ? {} : { detail: given }),
			group: groupTitleKey,
			icon: icon ?? "hash",
		}
	}
	const kind = byAddress.get(target.to)?.kind
	const direct = kind === "dm" || kind === "groupDm"
	const person = target.userId !== undefined
	const named =
		(kind === "channel" || kind === "private") &&
		given !== undefined &&
		squash(given) !== squash(rest.label)
	const label = named && given !== undefined ? given : rest.label
	const second = named ? rest.label : given
	return {
		...rest,
		label,
		...(second !== undefined && squash(second) !== squash(label) ? { detail: second } : {}),
		group: person || direct ? "people" : "channels",
		icon: kind === "private" ? "lock" : person || direct ? "people" : "hash",
	}
}

/**
 * A name with what a slug spells differently taken out of it, which is what makes "#design" and
 * "Design" the same word — and leaves "🎉 Design review" a different one.
 */
function squash(text: string): string {
	return text.toLowerCase().replaceAll(/[\s#_-]/g, "")
}

/** True while `path` is the row's address or somewhere inside it — a permalink is its conversation. */
function inside(path: string, to: string | undefined): boolean {
	return to !== undefined && (path === to || path.startsWith(`${to}/`))
}

/**
 * The rows as the list shows them: the five places most recently visited first, then every other row
 * under the group it belongs to, in the order the providers ranked them, and `limit` rows in all. A
 * row appears once — being recent is where it is drawn, not a second copy of it — and core's own
 * pages are never "recent", because *Recent* is what a palette is opened for and Settings is not a
 * place one returns to.
 *
 * The five are chosen before the limit is applied, so that the conversation somebody was in a minute
 * ago is at the top even when it stands two hundred rows down the list a provider handed over.
 */
export function groupRows(
	rows: SwitchRow[],
	recentPaths: readonly string[],
	limit = MAX_ROWS,
): RowGroup[] {
	const recent: SwitchRow[] = []
	const taken = new Set<string>()
	for (const path of recentPaths) {
		if (recent.length === RECENT_SHOWN) break
		const row = rows.find(
			(candidate) =>
				candidate.group !== "places" && !taken.has(candidate.id) && inside(path, candidate.to),
		)
		if (row === undefined) continue
		taken.add(row.id)
		recent.push({ ...row, group: "recent" })
	}
	// Unread first inside *Recent*: of the five places this reader keeps coming back to, the one with
	// something waiting in it is the one they are going to (X-B1 A2). The visit order survives inside
	// each half, because the sort is stable and asks one question.
	recent.sort((left, right) => Number((right.count ?? 0) > 0) - Number((left.count ?? 0) > 0))
	const byGroup = new Map<string, SwitchRow[]>([["recent", recent]])
	let room = limit - recent.length
	for (const row of rows) {
		if (taken.has(row.id) || room <= 0) continue
		room -= 1
		const group = byGroup.get(row.group) ?? []
		group.push(row)
		byGroup.set(row.group, group)
	}
	// Core's four groups in their own order first, then whatever a module headed for itself, in the
	// order its rows arrived: a new kind of destination joins the list without core knowing its name.
	const ordered = [
		...GROUPS,
		...[...byGroup.keys()].filter((group) => !GROUPS.includes(group as SwitchGroup)),
	]
	return ordered
		.map((group) => ({ group, titleKey: titleOfGroup(group), rows: byGroup.get(group) ?? [] }))
		.filter((entry) => entry.rows.length > 0)
}

export function QuickSwitcher({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	// Where the reader has been is noted here rather than in the list, because the list exists only
	// while the palette is open and the journey it ranks happened before that.
	const pathname = useRouterState({ select: (state) => state.location.pathname })
	useEffect(() => {
		noteVisited(pathname)
	}, [pathname])

	return (
		<Dialog open={open} onOpenChange={onOpenChange} title={t("shell.quickSwitcher.title")}>
			{/* Mounted only while it is open, so every opening starts on an empty term without a
			    single line of reset code. */}
			{open ? <Switcher onDone={() => onOpenChange(false)} /> : null}
		</Dialog>
	)
}

function Switcher({ onDone }: { onDone: () => void }) {
	const providers = useSlot("quickSwitcherProviders")
	const navigate = useNavigate()
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	const conversations = useModel((model) => model.conversations)
	const order = useModel((model) => model.conversationOrder)
	const openSheet = useOpenShortcutSheet()
	const [term, setTerm] = useState("")
	const [cursor, setCursor] = useState(0)
	const [fromModules, setFromModules] = useState<SwitchTarget[]>([])
	const listId = useId()

	// Read once, when the palette opens: this component is mounted by the opening, and where the
	// reader has been cannot change while it is up. Storage on every keystroke would be a synchronous
	// read in the typing path for an answer that is the same every time.
	const recent = useMemo(() => visitedPaths(), [])

	// Core's own destinations, named exactly as the rail and the menus name them.
	const local = useMemo<SwitchRow[]>(
		() =>
			CORE_DESTINATIONS.filter((item) => !("adminOnly" in item) || readerIsAdmin).map((item) => ({
				id: item.id,
				label: t(item.labelKey),
				group: "places" as const,
				icon: item.icon,
				...("to" in item ? { to: item.to } : { run: openSheet }),
				...("prepare" in item ? { prepare: () => setPendingFilter(FILTER_REPLIES) } : {}),
			})),
		[readerIsAdmin, openSheet],
	)

	// A provider may reach the network, so its answers arrive after the local ones and are merged
	// when they do; a stale answer to an older term is dropped rather than shown.
	useEffect(() => {
		let current = true
		void Promise.all(providers.map((provider) => provider.query(term)))
			.then((results) => {
				// The registry spells the provider's row shape out rather than importing this file's
				// (it is below the shell in the layering), so its `icon` is a `string` where the slot
				// that draws it takes an `IconName`. The name is checked where it is drawn: an icon a
				// build does not carry is nothing, not a crash (`icons.tsx`).
				if (current) setFromModules(results.flat() as SwitchTarget[])
			})
			.catch(() => {
				if (current) setFromModules([])
			})
		return () => {
			current = false
		}
	}, [term, providers])

	// The conversations the reader is *in*, by the address they are reached at: what tells a row's
	// glyph, its group and its two halves apart without asking a provider to say which kind of thing
	// it handed over. `conversationOrder` and not every conversation the model holds, because a
	// channel that is in the model without a membership is one nobody has joined — the row whose
	// second half is a status and not a name.
	const byAddress = useMemo(() => {
		const map = new Map<string, ConversationView>()
		for (const id of order) {
			const meta = conversations[id]?.meta
			const href = meta === undefined ? null : conversationHref(meta)
			if (meta !== undefined && href !== null) map.set(href, meta)
		}
		return map
	}, [order, conversations])

	// Core's own places after the providers' rows: a module's rows are the ones this palette is opened
	// for, and inside a band nothing is re-ranked (`rankRows`), so the order the rows arrive in is the
	// order they are offered in. Which group they are *drawn* under is `groupRows`'s answer.
	const groups = useMemo(
		() =>
			groupRows(
				rankRows([...fromModules.map((target) => describeRow(target, byAddress)), ...local], term),
				recent,
			),
		[local, fromModules, byAddress, term, recent],
	)

	// The flat list the keyboard walks: the groups are where a row is drawn, not a second dimension
	// to move in, so Down goes to the next row whichever heading it is under.
	const results = useMemo(() => groups.flatMap((entry) => entry.rows), [groups])

	// The cursor is clamped rather than reset from an effect: the result list shrinks as the term
	// grows, and a selection that points past the end must never be rendered.
	const at = Math.min(cursor, Math.max(results.length - 1, 0))
	const selected = results[at]

	// The switcher closes before the row acts, so a row that opens another overlay — the keyboard
	// sheet — is not opening it underneath this dialog.
	function go(target: SwitchRow | undefined) {
		if (!target) return
		onDone()
		target.prepare?.()
		if (target.run !== undefined) target.run()
		else if (target.to !== undefined) void navigate({ to: target.to })
	}

	return (
		<div className="tb-switcher">
			<div className="tb-switcher__search">
				<Icon name="search" />
				<input
					className="tb-switcher__input tb-field__control"
					type="text"
					role="combobox"
					autoComplete="off"
					aria-label={t("shell.quickSwitcher.label")}
					aria-expanded={results.length > 0}
					aria-controls={listId}
					aria-activedescendant={selected ? `${listId}-${selected.id}` : undefined}
					aria-describedby={`${listId}-hint`}
					placeholder={t("shell.quickSwitcher.placeholder")}
					value={term}
					onChange={(event) => {
						setTerm(event.target.value)
						setCursor(0)
					}}
					onKeyDown={(event) => {
						if (event.key === "ArrowDown") {
							event.preventDefault()
							setCursor(Math.min(at + 1, results.length - 1))
						} else if (event.key === "ArrowUp") {
							event.preventDefault()
							setCursor(Math.max(at - 1, 0))
						} else if (event.key === "Enter") {
							event.preventDefault()
							go(selected)
						}
					}}
				/>
			</div>
			{/* What the footer says in marks, for a reader who is not looking at it. */}
			<p className="tb-visually-hidden" id={`${listId}-hint`}>
				{t("shell.quickSwitcher.hint")}
			</p>
			{/* A listbox of divs rather than a <ul>: the rows are options in the input's combobox, and
			    the keyboard drives them through aria-activedescendant, not through focus. Each run of
			    rows is a `group` labelled by its heading, which is the one child a listbox may carry
			    besides an option. */}
			<div className="tb-switcher__list" id={listId} role="listbox">
				{results.length === 0 ? (
					<p className="tb-empty">{t("shell.quickSwitcher.empty")}</p>
				) : (
					groups.map((entry) => (
						<div key={entry.group} role="group" aria-labelledby={`${listId}-${entry.group}-title`}>
							{/* `translate` and not `t`: a module's heading is a key of its own namespace, and
							    the catalogue this palette is checked against is core's. */}
							<div className="tb-switcher__group" id={`${listId}-${entry.group}-title`}>
								{translate(entry.titleKey)}
							</div>
							{entry.rows.map((row) => (
								/* An aria-activedescendant listbox keeps focus in the input (WAI-ARIA APG), so
								   an option is deliberately not focusable and answers no key of its own: the
								   input's onKeyDown drives the selection. The pointer handlers are the mouse's
								   path to the same action. */
								// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
								<div
									key={row.id}
									id={`${listId}-${row.id}`}
									role="option"
									aria-selected={selected?.id === row.id}
									// A row with a count is named with the count in words; the pill below says the
									// same number to the eye. Without a count the name is the row's own text.
									{...(row.count === undefined || row.count <= 0
										? {}
										: {
												"aria-label": t("shell.nav.unread", { label: row.label, count: row.count }),
											})}
									className={
										selected?.id === row.id
											? "tb-switcher__row tb-switcher__row--on"
											: "tb-switcher__row"
									}
									onClick={() => go(row)}
									onMouseEnter={() => setCursor(results.findIndex((it) => it.id === row.id))}
								>
									<RowSlot row={row} />
									{/* A channel named with an emoji was tofu here, because a label arrives
									    already composed and carries no face of its own (D-01 §4.9). */}
									<span className="tb-switcher__label">{emojiText(row.label)}</span>
									<RowPill row={row} />
									{row.detail === undefined ? null : (
										<span className="tb-switcher__detail">{emojiText(row.detail)}</span>
									)}
									{/* What is waiting behind the row — the sidebar's own pill, in the accent when
									    some of it names the reader (X-B1 A2, G-71). It is hidden from assistive
									    technology because the option's name says the number in words. */}
									{row.count === undefined || row.count <= 0 ? null : (
										<span
											className={`tb-pill ${row.mention === true ? "tb-pill--mention" : "tb-pill--count"} tb-switcher__badge`}
											aria-hidden="true"
										>
											{formatNumber(row.count)}
										</span>
									)}
								</div>
							))}
						</div>
					))
				)}
			</div>
			<div className="tb-switcher__footer" aria-hidden="true">
				<span className="tb-switcher__keys">
					<kbd>{KEY_MARKS.enter}</kbd> {t("shell.quickSwitcher.key.open")}
				</span>
				<span className="tb-switcher__keys">
					<kbd>{KEY_MARKS.up}</kbd>
					<kbd>{KEY_MARKS.down}</kbd> {t("shell.quickSwitcher.key.move")}
				</span>
				<span className="tb-switcher__keys">
					<kbd>{t("shell.quickSwitcher.key.escape")}</kbd> {t("shell.quickSwitcher.key.close")}
				</span>
			</div>
		</div>
	)
}

/**
 * The pill of a person's row — *Guest* above all: the switcher is the shortest way to writing to
 * somebody, and somebody outside the team is named so before the conversation opens.
 */
function RowPill({ row }: { row: SwitchRow }) {
	const person = useModel((model) =>
		row.userId === undefined ? undefined : model.users[row.userId],
	)
	return <AccountPill user={person} />
}

/**
 * The 24 px slot, which every row has and which is what makes a list of channels, people and pages
 * scannable: the person's own face where the row is a person, the glyph of the kind of place it is
 * otherwise — and nothing at all for a channel, whose hash is part of the name the row prints, the
 * way the sidebar spells it (X-A3 V21: one object, one spelling). The slot stays: it is the line
 * every name in the list starts on.
 */
function RowSlot({ row }: { row: SwitchRow }) {
	const person = useModel((model) =>
		row.userId === undefined ? undefined : model.users[row.userId],
	)
	if (person === undefined && row.icon === "hash") return <span className="tb-switcher__slot" />
	return (
		<span className="tb-switcher__slot">
			{person === undefined ? (
				<Icon name={row.icon} />
			) : (
				// Inside a dialog, and under a pointer that is aiming at a row: no card on hover, and no
				// second way to the profile — Enter on the row goes to the conversation.
				<PersonFace person={chipPerson(person)} size="sm" hover={false} />
			)}
		</span>
	)
}
